import EventEmitter from "eventemitter3";
import LegacyDispatcher from "~/lib/legacyDispatcher";

type HighlightedRange = {
  jobId: string;
  start: number;
  end: number;
};

class JobLogLineStore {
  eventEmitter: EventEmitter;
  state: {
    [key: string]: boolean;
  };
  // Store for range-based highlighting (multi-line selection)
  highlightedRange: HighlightedRange | null;
  // Anchor line for shift+click range selection
  anchorLine: { jobId: string; lineNumber: number } | null;

  constructor() {
    this.eventEmitter = new EventEmitter();
    this.state = {};
    this.highlightedRange = null;
    this.anchorLine = null;
    LegacyDispatcher.on("job_log_line:highlight", ({ lineId }) => this.highlight(lineId));
  }

  addChangeListener(lineId: string, callback: () => void) {
    return this.eventEmitter.addListener(`${lineId}/change`, callback);
  }

  removeChangeListener(lineId: string, callback: () => void) {
    return this.eventEmitter.removeListener(`${lineId}/change`, callback);
  }

  getLineNumberFromId(lineId: string): number | null {
    // Content line format: jobId/groupNumber-lineNumber (e.g., "abc123/5-42")
    const contentLineMatch = lineId.match(/-(\d+)$/);
    if (contentLineMatch) {
      return parseInt(contentLineMatch[1], 10);
    }

    // Group header format: jobId/groupNumber (e.g., "abc123/53")
    const groupHeaderMatch = lineId.match(/\/(\d+)$/);
    if (groupHeaderMatch) {
      return parseInt(groupHeaderMatch[1], 10);
    }

    return null;
  }

  getJobIdFromLineId(lineId: string): string {
    // Format: jobId/groupNumber-lineNumber (e.g., "abc123/5-42")
    const [jobId] = lineId.split("/");
    return jobId;
  }

  /**
   * Check if a line is highlighted.
   * Supports both legacy single-line highlighting and range highlighting.
   */
  isHighlighted(lineId: string): boolean {
    // First check legacy single-line state
    if (this.state[lineId]) {
      return true;
    }

    // Then check range highlighting
    if (this.highlightedRange) {
      const jobId = this.getJobIdFromLineId(lineId);
      if (jobId !== this.highlightedRange.jobId) {
        return false;
      }

      const lineNumber = this.getLineNumberFromId(lineId);
      if (lineNumber === null) {
        return false;
      }

      return lineNumber >= this.highlightedRange.start && lineNumber <= this.highlightedRange.end;
    }

    return false;
  }

  /**
   * Get the currently highlighted range for a job.
   * Returns null if no range is highlighted or if the job doesn't match.
   */
  getHighlightedRange(jobId: string): { start: number; end: number } | null {
    if (this.highlightedRange && this.highlightedRange.jobId === jobId) {
      return {
        start: this.highlightedRange.start,
        end: this.highlightedRange.end,
      };
    }
    return null;
  }

  /**
   * Check if a line number falls within the currently highlighted multi-line range.
   * Returns false for single-line selections (where start === end).
   */
  isLineInRange(jobId: string, lineNumber: number): boolean {
    const range = this.getHighlightedRange(jobId);
    if (!range || range.start === range.end) {
      return false;
    }
    return lineNumber >= range.start && lineNumber <= range.end;
  }

  /**
   * Get the anchor line for shift+click range selection.
   */
  getAnchorLine(): { jobId: string; lineNumber: number } | null {
    return this.anchorLine;
  }

  /**
   * Set the anchor line for shift+click range selection.
   */
  setAnchorLine(jobId: string, lineNumber: number) {
    this.anchorLine = { jobId, lineNumber };
  }

  /**
   * Highlight a range of lines within a job.
   * Clears any existing highlights for this job.
   */
  highlightRange(jobId: string, startLine: number, endLine: number) {
    // Normalize to ascending order
    const start = Math.min(startLine, endLine);
    const end = Math.max(startLine, endLine);

    this.highlightedRange = { jobId, start, end };

    // Clear any legacy single-line highlights for this job
    for (const key in this.state) {
      if (key.startsWith(jobId)) {
        delete this.state[key];
        this.eventEmitter.emit(`${key}/change`);
      }
    }

    this.eventEmitter.emit(`${jobId}/range-change`);
  }

  /**
   * Clear any highlighted range.
   */
  clearRange() {
    if (this.highlightedRange) {
      const { jobId } = this.highlightedRange;
      this.highlightedRange = null;
      this.eventEmitter.emit(`${jobId}/range-change`);
    }
  }

  /**
   * Legacy single-line highlight function.
   * When multi-line selection is disabled or for backward compatibility.
   */
  highlight(lineId: string) {
    // If the line is already highlighted, toggle it off.
    if (this.state[lineId]) {
      delete this.state[lineId];
      this.eventEmitter.emit(`${lineId}/change`);
      return;
    }

    // Line ID's are in a format of: `jobId/lineNumber`
    const [jobId] = lineId.split("/");

    // Clear any range highlight for this job
    if (this.highlightedRange && this.highlightedRange.jobId === jobId) {
      this.highlightedRange = null;
      this.eventEmitter.emit(`${jobId}/range-change`);
    }

    // Remove the highlight from the other line from this job
    for (const key in this.state) {
      if (key.indexOf(jobId) === 0) {
        delete this.state[key];
        this.eventEmitter.emit(`${key}/change`);
        break;
      }
    }

    // Highlight the new line and emit the change
    this.state[lineId] = true;

    // Set this as the anchor line for potential shift+click
    const lineNumber = this.getLineNumberFromId(lineId);
    if (lineNumber !== null) {
      this.setAnchorLine(jobId, lineNumber);
    }

    return this.eventEmitter.emit(`${lineId}/change`);
  }

  /**
   * Add listener for range change events.
   */
  addRangeChangeListener(jobId: string, callback: () => void) {
    return this.eventEmitter.addListener(`${jobId}/range-change`, callback);
  }

  /**
   * Remove listener for range change events.
   */
  removeRangeChangeListener(jobId: string, callback: () => void) {
    return this.eventEmitter.removeListener(`${jobId}/range-change`, callback);
  }
}

export default new JobLogLineStore();
